import { UseTranslationResponse } from 'react-i18next';
import { AppName } from './types';
import { DotsRoles } from '@ab-inbev/sam-auth';

// TODO: Create a new types package and move these to it
enum GenericRoles {
  AppAllAdmin = 'App.All.Admin'
}

enum SpecialsRoles {
  ZoneSupplyUser = 'App.BrewingSpecials.ZoneSupplyUser',
  ZoneSupplyApprover = 'App.BrewingSpecials.ZoneSupplyApprover',
  BreweryUser = 'App.BrewingSpecials.BreweryUser',
  BreweryConfidentialUser = 'App.BrewingSpecials.ConfidentialUser',
  BreweryUserElevated = 'App.BrewingSpecials.BreweryUserElevated'
}

enum RecipesRoles {
  HopsRecipeAdmin = 'App.BrewingRecipes.HopRecipeAdmin',
  HopsRecipeEditor = 'App.BrewingRecipes.HopRecipeEditor',
  HopsRecipeViewer = 'App.BrewingRecipes.HopRecipeViewer'
}

enum SparePartsRoles {
  SparePartsUser = 'App.SpareParts.User'
}

enum CdcManagementRoles {
  CdcManagementViewer = 'App.CdcManagement.Viewer',
  CdcManagementAdmin = 'App.CdcManagement.Admin'
}

const breweryRoleToBreweryCodeMap: Record<string, string> = {
  'App.All.Brewery.Bldv': 'BVL',
  'App.All.Brewery.Cre': 'CRE',
  'App.All.Brewery.Crtv': 'CVL',
  'App.All.Brewery.Cob': 'COL',
  'App.All.Brewery.Edm': 'EDM',
  'App.All.Brewery.Fcl': 'FCL',
  'App.All.Brewery.Ffd': 'FFD',
  'App.All.Brewery.Hfx': 'HAL',
  'App.All.Brewery.Htn': 'HTN',
  'App.All.Brewery.Jksv': 'JAX',
  'App.All.Brewery.Lab': 'LA',
  'App.All.Brewery.Ldn': 'LON',
  'App.All.Brewery.Mer': 'MER',
  'App.All.Brewery.Nwk': 'NWK',
  'App.All.Brewery.Slb': 'STL',
  'App.All.Brewery.Mtl': 'MTL',
  'App.All.Brewery.RPB': 'RPB',
  'App.All.Brewery.Stj': 'STJ',
  'App.All.Brewery.Wms': 'WMS'
};

const roleToI18nKeyMap: Record<string, string> = {
  [RecipesRoles.HopsRecipeAdmin]: 'roles.hopsRecipeAdmin',
  [RecipesRoles.HopsRecipeEditor]: 'roles.hopsRecipeEditor',
  [RecipesRoles.HopsRecipeViewer]: 'roles.hopsRecipeViewer',
  [SpecialsRoles.ZoneSupplyApprover]: 'roles.zoneSupplyApprover',
  [SpecialsRoles.ZoneSupplyUser]: 'roles.zoneSupplyUser',
  [SpecialsRoles.BreweryUser]: 'roles.breweryUser',
  [SpecialsRoles.BreweryConfidentialUser]: 'roles.breweryConfidentialUser',
  [SparePartsRoles.SparePartsUser]: 'roles.sparePartsUser',
  [CdcManagementRoles.CdcManagementViewer]: 'roles.cdcManagementViewer',
  [CdcManagementRoles.CdcManagementAdmin]: 'roles.cdcManagementAdmin',
  [DotsRoles.LiveDotsConfigurationEditor]: 'roles.liveDotsConfigurationEditor',
  [DotsRoles.KpiCalcAdmin]: 'roles.kpiCalcAdmin',
  [DotsRoles.KpiCalcEditor]: 'roles.kpiCalcEditor',
  [GenericRoles.AppAllAdmin]: 'roles.appAllAdmin'
};

export const mapBreweryRoleToBreweryCode = (breweryRole: string): string => {
  return breweryRoleToBreweryCodeMap[breweryRole] ?? '';
};

const buildFilterRegex = (appName: AppName): RegExp => {
  switch (appName) {
    case AppName.BrewingSpecials:
      return /^App\.(BrewingSpecials\.|All\.Admin)/;
    case AppName.BrewingRecipes:
      return /^App\.(BrewingRecipes\.|All\.Admin)/;
    case AppName.SparePartsCockpit:
      return /^App\.(SpareParts\.|All\.Admin)/;
    case AppName.SupplyDataManagementPortal:
      return /^App\.((LiveDotsConfiguration|KPICalc)\.|All\.Admin)/;
    default:
      return /^$/;
  }
};

const filterRoles = (roles: string[], appName: AppName): string[] => {
  const regex = buildFilterRegex(appName);
  return roles.filter((role) => regex.test(role));
};

export const getRoleNames = ({
  userRoles,
  appName,
  permissionOrder,
  i18next
}: {
  userRoles: string[];
  appName: AppName;
  permissionOrder?: string[];
  i18next: UseTranslationResponse<'translation', undefined>;
}) => {
  let appRoles = filterRoles(userRoles, appName);
  if (permissionOrder) {
    const mainRole = permissionOrder.find((role) => appRoles.includes(role));
    appRoles = mainRole ? [mainRole] : appRoles;
  }

  return appRoles.map((role) => i18next.t(roleToI18nKeyMap[role])).join(' & ');
};
